import type { ColorDto, LibraryColorDto, SetColorArgs, SwatchArg } from '@/ApiClient';
import { colorToDraft, createColorDraft, draftToValues, type ColorDraft } from '@lib/colorManagement';

/**
 * The picker's rule for whether a save reuses a colour or makes a new one.
 *
 * Colours are shared, so editing one in the picker must not rewrite it for everyone else
 * using it. Picking an existing colour keeps a reference to it; touching any of its
 * values drops that reference, and the save asks the server to edit the colour or fork it
 * depending on whether anything else uses it.
 *
 * The server decides that, not the picker: usage counts are not loaded here and would be
 * stale by the time the save landed. Editing a colour others share is deliberately still not
 * possible from here; that is what the Colors page is for.
 */
export interface ColorPickerDraft {
  draft: ColorDraft;
  /** The colour this draft still refers to, or null once the user has changed something. */
  sourceColorId: number | null;
  /**
   * The colour this draft started from, kept through edits. It is what an edited save offers the
   * server to rename — {@link sourceColorId} cannot serve, because an edit is exactly when it
   * goes null.
   */
  anchorColorId: number | null;
  /** A library colour to adopt on save, when the user picked one that the brand has no copy of. */
  sourceLibraryColorId: number | null;
  /**
   * The library entry the values come from, kept through edits. An edited save adopts it with the
   * edits, so a name or code of the brand's own does not cost the colour its library.
   */
  libraryColorId: number | null;
  /** What the draft was opened on or picked from, kept so that undoing every edit can refer to it again. */
  origin: ColorOrigin | null;
  /** What the picker shows about where these values came from. Never sent. */
  provenance: ColorProvenance;
  /** The picture the colour is drawn with, which the user may replace or go back to the flat colour from. */
  swatch: PickerSwatch;
}

/**
 * The picture a colour is drawn with in the picker. `uploadedFileId` is a picture dropped in here and not
 * saved yet; without one, `url` is the colour's own swatch, or null for its flat colour.
 */
export interface PickerSwatch {
  url: string | null;
  uploadedFileId: number | null;
}

const noSwatch = (): PickerSwatch => ({ url: null, uploadedFileId: null });

/**
 * Where the draft's values came from, for the picker to show. Read-only: a colour number is the
 * brand's to allocate and a library entry is nobody's to rewrite from here.
 */
export interface ColorProvenance {
  /** The colour number of the entity behind the draft, or null when there is no entity yet. */
  number: string | null;
  libraryName: string | null;
  libraryCode: string | null;
  /** The library's own name for the colour, which the brand may have renamed. */
  libraryColorName: string | null;
}

/** A colour the draft can refer to, and the values that reference stands for. */
export interface ColorOrigin {
  colorId: number | null;
  libraryColorId: number | null;
  values: ColorDraft;
  /** The swatch picture it is drawn with, or null for its flat colour. */
  swatchUrl: string | null;
}

const noProvenance = (): ColorProvenance => ({
  number: null,
  libraryName: null,
  libraryCode: null,
  libraryColorName: null,
});

/** The colour number and library entry behind a saved colour. */
const provenanceOf = (color: ColorDto): ColorProvenance => ({
  number: color.number,
  libraryName: color.libraryName ?? null,
  libraryCode: color.libraryColorCode ?? null,
  libraryColorName: color.libraryColorName ?? null,
});

export const emptyDraft = (): ColorPickerDraft => ({
  draft: createColorDraft(),
  sourceColorId: null,
  anchorColorId: null,
  sourceLibraryColorId: null,
  libraryColorId: null,
  origin: null,
  provenance: noProvenance(),
  swatch: noSwatch(),
});

/** Opens the picker on a colour that is already set. */
export const draftFromColor = (color: ColorDto | null | undefined): ColorPickerDraft =>
  color ? pickExisting(color) : emptyDraft();

/**
 * A colour picked from a fixed set with no entity behind it — the base-colour swatches.
 * There is nothing to refer to, so it saves as a new colour.
 */
export const draftFromValues = (values: Partial<ColorDraft>): ColorPickerDraft => ({
  draft: createColorDraft(values),
  sourceColorId: null,
  anchorColorId: null,
  sourceLibraryColorId: null,
  libraryColorId: null,
  origin: null,
  provenance: noProvenance(),
  swatch: noSwatch(),
});

/**
 * An existing colour used as the starting point for a new one: its values, and no reference to it,
 * so saving makes a colour of its own whether or not anything was changed. A colour adopted from a
 * library keeps its library, so the new colour is another adoption of the same entry.
 */
export const copyOf = (color: ColorDto): ColorPickerDraft => {
  const { __draftKey: _, ...values } = colorToDraft(color);
  return {
    ...draftFromValues(values),
    libraryColorId: color.libraryColorId ?? null,
    // No number: the new colour gets its own when it is saved.
    provenance: { ...provenanceOf(color), number: null },
  };
};

/** The user chose an existing colour from search — reuse that exact entity. */
export const pickExisting = (color: ColorDto): ColorPickerDraft => ({
  draft: colorToDraft(color),
  sourceColorId: color.id,
  anchorColorId: color.id,
  sourceLibraryColorId: null,
  libraryColorId: null,
  origin: { colorId: color.id, libraryColorId: null, values: colorToDraft(color), swatchUrl: color.swatchUrl ?? null },
  provenance: provenanceOf(color),
  swatch: { url: color.swatchUrl ?? null, uploadedFileId: null },
});

const libraryColorToDraft = (libraryColor: LibraryColorDto): ColorDraft =>
  createColorDraft({
    name: libraryColor.name,
    code: libraryColor.code,
    c: libraryColor.c,
    m: libraryColor.m,
    y: libraryColor.y,
    k: libraryColor.k,
    r: libraryColor.r,
    g: libraryColor.g,
    b: libraryColor.b,
  });

/** The user chose a library colour the brand has no copy of — adopt it on save. */
export const pickFromLibrary = (libraryColor: LibraryColorDto): ColorPickerDraft => ({
  draft: libraryColorToDraft(libraryColor),
  sourceColorId: null,
  anchorColorId: null,
  sourceLibraryColorId: libraryColor.id,
  libraryColorId: libraryColor.id,
  origin: {
    colorId: null,
    libraryColorId: libraryColor.id,
    values: libraryColorToDraft(libraryColor),
    swatchUrl: null,
  },
  // Not adopted yet, so it has no number of the brand's own; it gets one when this saves.
  provenance: {
    number: null,
    libraryName: libraryColor.libraryName,
    libraryCode: libraryColor.code,
    libraryColorName: libraryColor.name,
  },
  swatch: noSwatch(),
});

const sameValues = (a: ColorDraft, b: ColorDraft, { compareGroup }: { compareGroup: boolean }) =>
  a.name === b.name &&
  (a.code || '') === (b.code || '') &&
  (!compareGroup || a.groupId === b.groupId) &&
  a.c === b.c &&
  a.m === b.m &&
  a.y === b.y &&
  a.k === b.k &&
  a.r === b.r &&
  a.g === b.g &&
  a.b === b.b;

/**
 * What the draft refers to after the user changed something: nothing while any value differs
 * from what it was opened on or picked from, and that again once every value matches it —
 * undoing an edit, or resetting a renamed library colour's name, reuses the colour rather than
 * saving a copy of it.
 *
 * The group counts only when the user chose it. The picker fills one in for a colour without,
 * which must not read as an edit; and adopting a library colour does not take the picker's
 * group anyway.
 */
export const referenceAfterEdit = (
  state: ColorPickerDraft,
  { userChoseGroup }: { userChoseGroup: boolean }
): Pick<ColorPickerDraft, 'sourceColorId' | 'sourceLibraryColorId'> => {
  const matches =
    state.origin &&
    sameValues(state.draft, state.origin.values, { compareGroup: userChoseGroup }) &&
    !swatchChanged(state);

  return {
    sourceColorId: matches ? state.origin!.colorId : null,
    sourceLibraryColorId: matches ? state.origin!.libraryColorId : null,
  };
};

/**
 * The user changed a value, so the draft stops referring to whatever it came from — unless the
 * change put every value back, see {@link referenceAfterEdit}.
 *
 * Only call this for edits the user actually made. Anything the picker fills in on their
 * behalf — the nearest colour group for an ungrouped colour, say — must go through
 * {@link applyImplicit} instead, or merely opening the picker would fork the colour.
 */
export const applyEdit = (state: ColorPickerDraft, changes: Partial<ColorDraft>): ColorPickerDraft => {
  const edited = { ...state, draft: { ...state.draft, ...changes } };
  return { ...edited, ...referenceAfterEdit(edited, { userChoseGroup: 'groupId' in changes }) };
};

/** Fills in a value on the user's behalf, keeping any reference intact. */
export const applyImplicit = (state: ColorPickerDraft, changes: Partial<ColorDraft>): ColorPickerDraft => ({
  ...state,
  draft: { ...state.draft, ...changes },
});

/**
 * The draft as the colour it will save as, for a list showing it before it is saved: its values and the
 * picture it is drawn with.
 */
export const draftAsColor = (state: ColorPickerDraft) => ({
  ...state.draft,
  swatchUrl: state.swatch.url ?? undefined,
});

/** Whether the user dropped in a picture, or went back to the flat colour from the one the draft came with. */
const swatchChanged = (state: ColorPickerDraft) =>
  state.swatch.uploadedFileId != null || (state.swatch.url == null) !== ((state.origin?.swatchUrl ?? null) == null);

/** The user dropped a picture in to draw the colour with — an edit, as changing a value is. */
export const withUploadedSwatch = (state: ColorPickerDraft, url: string, fileId: number): ColorPickerDraft => {
  const edited = { ...state, swatch: { url, uploadedFileId: fileId } };
  return { ...edited, ...referenceAfterEdit(edited, { userChoseGroup: false }) };
};

/** The user went back to the flat colour. The colour keeps its images; only the swatch changes. */
export const withFlatSwatch = (state: ColorPickerDraft): ColorPickerDraft => {
  const edited = { ...state, swatch: noSwatch() };
  return { ...edited, ...referenceAfterEdit(edited, { userChoseGroup: false }) };
};

/** The swatch change a save carries, or undefined when the swatch was left alone. */
const swatchArg = (state: ColorPickerDraft): SwatchArg | undefined =>
  swatchChanged(state) ? { azureBlobFileId: state.swatch.uploadedFileId ?? undefined } : undefined;

/** What to send when saving. */
export const toSetColorArgs = (state: ColorPickerDraft | null | undefined): SetColorArgs => {
  if (!state) return { type: 'NoColorSelected' } as SetColorArgs;

  if (state.sourceColorId != null) {
    return { type: 'ExistingColor', brandColorId: state.sourceColorId } as SetColorArgs;
  }

  if (state.sourceLibraryColorId != null) {
    return { type: 'AdoptedLibraryColor', libraryColorId: state.sourceLibraryColorId } as SetColorArgs;
  }

  // Edited a colour that already exists: the server renames it when nothing else uses it, and
  // creates a new one when something does.
  if (state.anchorColorId != null) {
    return {
      type: 'EditedColor',
      brandColorId: state.anchorColorId,
      values: draftToValues(state.draft),
      swatch: swatchArg(state),
    } as SetColorArgs;
  }

  // Edited a library colour the brand had not adopted: still that library colour under a name or
  // code of the brand's own, which the server keeps as overrides — unless the colour itself was
  // changed, which makes it one of the brand's own.
  if (state.libraryColorId != null) {
    return {
      type: 'AdoptedLibraryColor',
      libraryColorId: state.libraryColorId,
      values: draftToValues(state.draft),
      swatch: swatchArg(state),
    } as SetColorArgs;
  }

  return { type: 'NewColor', values: draftToValues(state.draft), swatch: swatchArg(state) } as SetColorArgs;
};

/** Shorthand for the common "reuse this saved colour" write. */
export const existingColorArgs = (color: ColorDto | null | undefined): SetColorArgs =>
  color
    ? ({ type: 'ExistingColor', brandColorId: color.id } as SetColorArgs)
    : ({ type: 'NoColorSelected' } as SetColorArgs);

export const noColorArgs = (): SetColorArgs => ({ type: 'NoColorSelected' }) as SetColorArgs;
